import React, { useEffect, useMemo, useRef } from 'react';
import { ArrowsFullscreen } from 'react-bootstrap-icons';
import { BASE_URL } from '../../config/api-routes';
import logo from '../../images/SKIP.svg';

type Props = {
  username: string;
  promptId: number;
  candidateName: string;
  shareToken?: string | null;
};

export default function ProtectedPromptVideo({
  username,
  promptId,
  candidateName,
  shareToken,
}: Props) {
  const shellRef = useRef<HTMLDivElement>(null);

  const mediaUrl = useMemo(() => {
    const base = `${BASE_URL}u/${encodeURIComponent(username)}/prompts/${promptId}/media`;
    if (!shareToken) return base;
    const params = new URLSearchParams({ recruitertoken: shareToken });
    return `${base}?${params.toString()}`;
  }, [username, promptId, shareToken]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const shell = shellRef.current;
      if (!shell) return;
      shell.classList.toggle(
        'protected-prompt-video--fullscreen',
        document.fullscreenElement === shell
      );
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    const shell = shellRef.current;
    if (!shell) return;
    try {
      if (document.fullscreenElement === shell) {
        await document.exitFullscreen();
      } else {
        await shell.requestFullscreen();
      }
    } catch {
      // Ignore unsupported fullscreen environments.
    }
  };

  return (
    <div
      ref={shellRef}
      className="protected-prompt-video"
      onContextMenu={(event) => event.preventDefault()}
    >
      <video
        key={mediaUrl}
        controls
        controlsList="nodownload noplaybackrate nofullscreen"
        disablePictureInPicture
        playsInline
        preload="metadata"
        className="protected-prompt-video__player"
      >
        <source src={mediaUrl} />
      </video>
      <button
        type="button"
        className="protected-prompt-video__fullscreen-btn"
        onClick={() => void toggleFullscreen()}
        aria-label="Enter fullscreen"
      >
        <ArrowsFullscreen size={14} />
      </button>
      <div className="protected-prompt-video__watermark" aria-hidden="true">
        <img src={logo} alt="" className="protected-prompt-video__logo" />
        <span>{candidateName}</span>
      </div>
    </div>
  );
}
